import Actor from "components/Activities/Actor";
import Subject from "components/Activities/Subject";
import { ErrorBoundary } from "components/Shared";
import { translate } from "lib";
import React from "react";

const SignSigner = ({ activity }: { activity: tcpinpoint.ActivityItem }) => {
	const content = activity.attributes.item.content;
	if (content) {
		// "ready to sign" notification. The presence of item.content flags this as
		// the ready-to-sign event (vs. the "signed" event below). We compose the
		// message from structured fields — rather than rendering the raw content
		// string — so the form name, project name and recipient pick up the same
		// activity-feed styling as every other item:
		//   - bare text ("form … is ready for … to sign") is lowercased by
		//     `.activity-item-body { text-transform: lowercase }`;
		//   - `.activity-item-context` / `.activity-item-subject` spans are
		//     capitalised and bolded, so the project name and "You" render
		//     consistently with the Party/Comment rows.
		const item = activity.attributes.item;
		const formName = item.subject?.name;
		const projectName = item.object?.project_name;
		// object.amended → "form amended" (re-generated after Undo);
		// object.replaced → a signer replaced their upload (names the replacer);
		// otherwise → "ready to sign".
		const amended = !!(item.object as any)?.amended;
		const replaced = !!(item.object as any)?.replaced;
		if (replaced) {
			// "<replacer> replaced form <name> on <project>" — the activity actor is
			// the person who replaced their signed copy.
			return (
				<ErrorBoundary>
					<Actor actor={item.actor} />
					{" replaced form "}
					{formName}
					{projectName ? (
						<>
							{" on "}
							<span className="activity-item-context">{projectName}</span>
						</>
					) : null}
				</ErrorBoundary>
			);
		}
		return (
			<ErrorBoundary>
				{/* Leading word displayed as "Form". `.activity-item-body` lowercases
				    bare text, so we override text-transform here; fontWeight 400 keeps
				    it in the normal sentence weight (the body's `span` rule would
				    otherwise bold it). textContent stays "form " for consistency. */}
				<span style={{ textTransform: "capitalize", fontWeight: 400 }}>{"form "}</span>
				{formName}
				{projectName ? (
					<>
						{" on "}
						<span className="activity-item-context">{projectName}</span>
					</>
				) : null}
				{amended ? (
					" has been amended"
				) : (
					<>
						{" is ready for "}
						<span className="activity-item-subject">You</span>
						{" to sign"}
					</>
				)}
			</ErrorBoundary>
		);
	}
	return (
		<ErrorBoundary>
			<Actor actor={activity.attributes.item.actor} /> {translate("signed")}{" "}
			<Subject subject={activity.attributes.item.subject} />{" "}
		</ErrorBoundary>
	);
};

export default SignSigner;
